import { useEffect, useMemo, useState } from 'react';
import { DataTable, DataTableColumn } from '@xtnd/web/shared/components/DataTable';
import { Icon, Tooltip, Badge } from '@repo/ui';
import { dateUtil, numberUtil } from '@xtnd/web/shared/utils/format';
import { DAYS_PAST_DUE } from '../../constants';
import { getRowUniqueKey, paginateLocal, searchTableLocal, sortLocal } from './utils';
import { ArEmailQueueMatter, EMAIL_SORT_BY } from '../../types/emailQueue';
import { useArEmailCollectionsStore } from '@xtnd/web/shared/stores';

interface MatterTableProps {
  renderHeader?: () => React.ReactNode;
  onClickExternal?: (row: ArEmailQueueMatter) => void;
  onRemoveFromEmailQueue?: (row: ArEmailQueueMatter) => void;
  onRowClick?: (row: ArEmailQueueMatter) => void;
  searchLocal: string;
  isLoading?: boolean;
}

interface ArMatterLocal {
  sorts: Partial<Record<EMAIL_SORT_BY, 'asc' | 'desc'>>;
  page: number;
  limit: number;
}

const LocalMatterTableQueryInit: ArMatterLocal = {
  page: 1,
  limit: 25,
  sorts: {
    maxDaysPastDue: 'desc',
  },
};

export function ArMatterChildTable({
  renderHeader,
  onClickExternal,
  onRemoveFromEmailQueue,
  onRowClick,
  searchLocal,
  isLoading = false,
}: MatterTableProps) {
  const { matters, selectedKeys, setSelectedKeys } = useArEmailCollectionsStore();
  const [tableParams, setTableParams] = useState(LocalMatterTableQueryInit);
  let filtered = searchTableLocal(matters ?? [], searchLocal);
  filtered = sortLocal(filtered, tableParams.sorts);
  const paginated = paginateLocal(filtered, tableParams.page, tableParams.limit);
  const totalPages = Math.ceil(filtered.length / tableParams.limit);
  useEffect(() => {
    if (tableParams.page > totalPages) {
      setTableParams(prev => ({
        ...prev,
        page: totalPages === 0 ? 1 : totalPages,
      }));
    }
  }, [filtered.length, totalPages]);

  useEffect(() => {
    setSelectedKeys([]);
  }, [searchLocal]);

  const handleRowCheckbox = (e: React.ChangeEvent<HTMLInputElement>, row: ArEmailQueueMatter) => {
    e.stopPropagation();
    const key = getRowUniqueKey(row);
    const next = e?.target?.checked ? [...selectedKeys, key] : selectedKeys.filter(k => k !== key);
    setSelectedKeys(next);
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e?.target?.checked;
    const keys = checked ? (paginated.data ?? []).map(ite => getRowUniqueKey(ite)) : [];
    setSelectedKeys(keys);
  };

  const columns: DataTableColumn<ArEmailQueueMatter>[] = useMemo(
    () => [
      {
        key: 'select',
        header: (
          <input
            type="checkbox"
            className="h-4 w-4 text-xtnd-blue border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-xtnd-blue cursor-pointer"
            onChange={handleSelectAll}
            checked={(matters?.length ?? 0) > 0 && selectedKeys?.length > 0}
          />
        ),
        align: 'left' as const,
        renderCell: row => (
          <input
            type="checkbox"
            className="h-4 w-4 text-xtnd-blue border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-xtnd-blue cursor-pointer"
            checked={selectedKeys.includes(getRowUniqueKey(row))}
            onChange={e => handleRowCheckbox(e, row)}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            onPointerDown={e => e.stopPropagation()}
          />
        ),
      },
      {
        key: 'matterDisplayNumber',
        header: 'Matter #',
        align: 'left' as const,
        isResizable: true,
        defaultWidth: 200,
        renderCell: row => (
          <Tooltip content={row?.matterDisplayNumber ?? ''} position="auto" className="rounded-md">
            <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
              {row?.matterDisplayNumber}
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'invoiceNumber',
        header: 'Invoice #',
        align: 'left' as const,
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <Tooltip
            contentClassName="min-w-[10rem] h-full"
            content={row?.invoiceNumbers?.join(', ') || ''}
            position="right"
          >
            <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light block">
              {row?.invoiceNumbers?.join(', ')}
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'amountOutstanding',
        header: 'Amount Outstanding',
        align: 'right' as const,
        isSortable: true,
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            {numberUtil.formatCurrency(row?.amountOutstanding)}
          </span>
        ),
      },
      {
        key: 'maxDaysPastDue',
        header: 'DPD',
        align: 'right',
        isSortable: true,
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            <div className={`text-sm ${row?.maxDaysPastDue > DAYS_PAST_DUE ? 'text-red-500' : ''}`}>
              {row?.maxDaysPastDue}
            </div>
          </div>
        ),
      },
      {
        key: 'clientEmail',
        header: 'Client Email',
        align: 'left' as const,
        isResizable: true,
        defaultWidth: 200,
        renderCell: row => {
          const hasClientEmail = row?.clientEmail;
          const hasAdjusterEmail = row?.matterAdjusterEmail;

          if (!hasClientEmail && !hasAdjusterEmail) {
            return (
              <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">-</span>
            );
          }

          return (
            <div className="flex flex-col gap-1">
              {hasClientEmail && (
                <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
                  {row.clientEmail}
                </span>
              )}
              {hasAdjusterEmail && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
                    {row.matterAdjusterEmail}
                  </span>
                  <Badge variant="primary" size="sm">
                    Adjuster
                  </Badge>
                </div>
              )}
            </div>
          );
        },
      },
      {
        key: 'lastFollowUpDate',
        header: 'Last Follow Up Date',
        align: 'left' as const,
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <Tooltip
            contentClassName="min-w-[10rem] h-full"
            content={
              (row?.lastFollowUpDate &&
                dateUtil.formatDate(row.lastFollowUpDate, dateUtil.DateFormat.US)) ||
              ''
            }
            position="right"
          >
            <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light max-w-[10rem] truncate block">
              {row?.lastFollowUpDate &&
                dateUtil.formatDate(row.lastFollowUpDate, dateUtil.DateFormat.US)}
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'hiExternalLink',
        header: '',
        align: 'center' as const,
        isResizable: true,
        defaultWidth: 150,
        renderCell: row => (
          <div className="relative min-w-[3rem] flex items-center justify-center gap-4">
            <div
              className="group flex items-center justify-center p-2 rounded-md border border-gray-200 dark:border-gray-700 hover:bg-xtnd-blue hover:border-xtnd-blue cursor-pointer transition-colors"
              onClick={e => {
                e.stopPropagation();
                onClickExternal?.(row);
              }}
            >
              <Icon
                name="HiExternalLink"
                className="text-sm text-xtnd-blue dark:text-xtnd-blue-400 group-hover:text-white"
              />
            </div>
            <div
              className="group flex items-center justify-center p-2 rounded-md border border-gray-200 dark:border-gray-700 hover:bg-red-500 hover:border-red-500 cursor-pointer transition-colors"
              title="Remove from email queue"
              onClick={e => {
                e.stopPropagation();
                onRemoveFromEmailQueue?.(row);
              }}
            >
              <Icon name="FaTrash" className="!h-4 !w-4 text-red-500 group-hover:text-white" />
            </div>
            {!row?.clientEmail && !row?.matterAdjusterEmail && (
              <div className="">
                <div className="relative left-0 group/warning inline-block text-center align-middle">
                  <Icon
                    name="HiExclamation"
                    className="!h-4 !w-4 text-amber-500"
                    title="No email contact"
                    onClick={e => e.stopPropagation()}
                  />
                  <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 hidden group-hover/warning:block z-50 w-72 bg-amber-50 dark:bg-amber-900/95 border border-amber-200 dark:border-amber-700 rounded-lg shadow-xl px-3 py-1">
                    <div className="text-xs text-left">
                      <div className="font-semibold text-amber-800 dark:text-amber-200 mb-2 flex items-center space-x-1">
                        <Icon name="HiExclamation" className="h-4 w-4" />
                        <span>Warnings</span>
                      </div>
                      <ul className="text-amber-700 dark:text-amber-300">
                        <li className="flex items-start space-x-2">
                          <span className="text-amber-600 dark:text-amber-400">•</span>
                          <span>The client does not have contact email</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ),
      },
    ],
    [matters, selectedKeys]
  );

  return (
    <DataTable
      columns={columns}
      data={{ data: paginated.data, total: filtered?.length ?? 0 }}
      pagination={paginated.pagination}
      onPaginationChange={pagination => {
        setSelectedKeys([]);
        setTableParams(prev => ({ ...prev, page: pagination.page, limit: pagination.limit }));
      }}
      stickyHeader
      tableContainerClassName="overflow-y-auto relative"
      tableClassName="min-w-full divide-y divide-gray-200 relative"
      renderHeader={renderHeader}
      onRowClick={onRowClick}
      getRowKey={row => getRowUniqueKey(row)}
      enableColumnResizing
      columnWidthsStorageKey={`email-matter-table`}
      onSort={sortBy => setTableParams({ ...tableParams, sorts: { ...sortBy } })}
      sorting={tableParams.sorts}
      isLoading={isLoading}
    />
  );
}
